// ═══════════════════════════════════════════════════════════════
// Battle Rhythm — Dashboard
// Overview stats, active activations, recent closures, create modal
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, SimpleGrid, Paper, Group, Badge, Stack, Button,
  Modal, TextInput, Select, Skeleton, ThemeIcon, Collapse, Center,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { DateTimePicker } from '@mantine/dates';
import {
  IconPlus, IconArrowRight, IconCalendarEvent, IconClock,
  IconTemplate, IconActivity, IconChevronDown, IconChevronUp,
  IconPresentation,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBattleRhythmStore } from '../../store/battlerhythmStore';
import { useAuthStore } from '../../store/authStore';
import type { Activation } from '../../store/battlerhythmStore';

export default function BRDashboard() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const {
    activations, events, templates, loading,
    loadActivations, loadEvents, loadTemplates,
    createActivation, createEvent,
  } = useBattleRhythmStore();

  // ── Modal state ─────────────────────────────────────────────
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [newOrg, setNewOrg] = useState('');
  const [opPeriod, setOpPeriod] = useState<string>('12');
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // ── Closed section ──────────────────────────────────────────
  const [closedOpen, { toggle: toggleClosed }] = useDisclosure(false);

  // ── Load data on mount / incident change ────────────────────
  const incidentId = activeIncident ? String(activeIncident.id) : '';

  useEffect(() => {
    if (incidentId) {
      loadActivations(incidentId);
      loadEvents(incidentId);
    }
    loadTemplates();
  }, [incidentId]);

  // ── No-incident guard ───────────────────────────────────────
  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="xs">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray">
            <IconCalendarEvent size={30} />
          </ThemeIcon>
          <Title order={3}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">Select an incident from the top bar to view the Battle Rhythm dashboard.</Text>
        </Stack>
      </Center>
    );
  }

  // ── Derived data ────────────────────────────────────────────
  const incActivations = activations.filter(a => String(a.incidentId) === incidentId);
  const activeActs = incActivations.filter(a => a.status === 'active');
  const closedActs = incActivations.filter(a => a.status === 'closed');

  const incEvents = events.filter(e => String(e.incidentId) === incidentId);

  // ── Stats ───────────────────────────────────────────────────
  const upcomingEvents = incEvents.filter(e => e.status === 'upcoming' || e.status === 'in-progress');
  const completedEvents = incEvents.filter(e => e.status === 'completed' || e.status === 'skipped');

  const stats = [
    { label: 'Active Activations', value: activeActs.length, color: '#8B5CF6', icon: IconActivity },
    { label: 'Scheduled Events', value: upcomingEvents.length, color: '#4a82c4', icon: IconCalendarEvent },
    { label: 'Completed', value: completedEvents.length, color: '#627d98', icon: IconClock },
    { label: 'Templates', value: templates.length, color: '#059669', icon: IconTemplate },
  ];

  // ── Format operational period ───────────────────────────────
  const formatOpPeriod = (act: Activation) => {
    if (!act.opStart) return `${act.opHours || '12'}h Op Period`;
    const start = dayjs(act.opStart);
    const hours = Number(act.opHours) || 12;
    const end = start.add(hours, 'hour');
    return `${start.format('MMM D, HH:mm')} — ${end.format('MMM D, HH:mm')} (${hours}h)`;
  };

  // ── Create activation handler ───────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    try {
      await createActivation({
        incidentId,
        org: newOrg.trim(),
        opHours: opPeriod,
        opStart: startTime ? startTime.toISOString() : new Date().toISOString(),
        status: 'active',
        creator: user?.fullName ?? user?.email ?? '',
        templateId: selectedTemplate || '',
      });

      // If a template was selected, create events from it
      if (selectedTemplate) {
        const tmpl = templates.find(t => String(t.id) === selectedTemplate);
        if (tmpl?.events) {
          try {
            const templateEvents = JSON.parse(tmpl.events) as Array<{
              title: string; category?: string; scheduledTime: string;
              durationMinutes?: number; location?: string; responsible?: string;
              notes?: string; recurring?: boolean;
            }>;

            for (const te of templateEvents) {
              await createEvent({
                incidentId,
                title: te.title,
                category: te.category ?? 'other',
                scheduledTime: te.scheduledTime,
                durationMinutes: String(te.durationMinutes ?? 30),
                location: te.location ?? '',
                responsible: te.responsible ?? '',
                notes: te.notes ?? '',
                recurring: te.recurring ? 'true' : 'false',
                adHoc: 'false',
                status: 'upcoming',
                creator: user?.fullName ?? user?.email ?? '',
              });
            }
          } catch (parseErr) {
            console.error('Failed to parse template events:', parseErr);
          }
        }
      }

      // Reset form & close
      setNewOrg('');
      setOpPeriod('12');
      setStartTime(null);
      setSelectedTemplate(null);
      closeCreate();

      // Reload data
      await loadActivations(incidentId);
      await loadEvents(incidentId);
    } catch (err) {
      console.error('Failed to create activation:', err);
    } finally {
      setCreating(false);
    }
  };

  // ── Template select options ─────────────────────────────────
  const templateOptions = templates.map(t => ({
    value: String(t.id),
    label: t.name,
  }));

  return (
    <div>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={3} c="black">Battle Rhythm</Title>
          <Text size="sm" c="dimmed">
            {activeIncident.name} — Operational tempo and scheduling
          </Text>
        </div>
        <Button
          size="sm"
          leftSection={<IconPlus size={14} />}
          onClick={openCreate}
        >
          New Activation
        </Button>
      </Group>

      {/* ── Stat Cards ─────────────────────────────────────────── */}
      {loading ? (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} height={100} radius="sm" />)}
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
          {stats.map(s => (
            <Paper key={s.label} p="sm" radius="sm" withBorder ta="center"
              style={{ borderTop: `3px solid ${s.color}` }}>
              <Text size="xs" c="dimmed">{s.label}</Text>
              <Text fw={700} fz={28} style={{ color: s.color }}>{s.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* ── Active Activations ─────────────────────────────────── */}
      <Title order={4} mb="sm">Active Activations</Title>

      {loading ? (
        <Stack gap="sm" mb="lg">
          {[1, 2].map(i => <Skeleton key={i} height={100} radius="sm" />)}
        </Stack>
      ) : activeActs.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder mb="lg">
          <Center>
            <Stack align="center" gap="xs">
              <Text size="sm" c="dimmed">No active activations. Create one to get started.</Text>
              <Button
                variant="light"
                color="violet"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={openCreate}
              >
                Create Activation
              </Button>
            </Stack>
          </Center>
        </Paper>
      ) : (
        <Stack gap="sm" mb="lg">
          {activeActs.map(act => (
            <Paper
              key={act.id}
              p="md"
              radius="sm"
              withBorder
              style={{ borderLeft: '3px solid #8B5CF6' }}
            >
              <Group justify="space-between" wrap="nowrap">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Group gap="xs" mb={4}>
                    <Text fw={600} size="md">{act.org || `${act.opHours}h Op Period`}</Text>
                    <Badge size="xs" color="green" variant="filled">Active</Badge>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" variant="outline" color="violet" leftSection={<IconClock size={10} />}>
                      {formatOpPeriod(act)}
                    </Badge>
                  </Group>
                </div>
                <Group gap="xs">
                  <Button
                    variant="light"
                    color="violet"
                    size="xs"
                    rightSection={<IconArrowRight size={14} />}
                    onClick={() => navigate(`/battlerhythm/activation/${act.id}`)}
                  >
                    View
                  </Button>
                  <Button
                    variant="subtle"
                    color="violet"
                    size="xs"
                    leftSection={<IconPresentation size={14} />}
                    onClick={() => navigate('/battlerhythm/display')}
                  >
                    Display
                  </Button>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {/* ── Recently Closed ────────────────────────────────────── */}
      {closedActs.length > 0 && (
        <>
          <Divider mb="sm" />
          <Button
            variant="subtle"
            color="gray"
            size="xs"
            mb="sm"
            rightSection={closedOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            onClick={toggleClosed}
          >
            Recently Closed ({closedActs.length})
          </Button>
          <Collapse in={closedOpen}>
            <Stack gap="sm" mb="lg">
              {closedActs.map(act => (
                <Paper
                  key={act.id}
                  p="md"
                  radius="sm"
                  withBorder
                  style={{ borderLeft: '3px solid #627d98', opacity: 0.8 }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Group gap="xs" mb={4}>
                        <Text fw={600} size="md">{act.org || `${act.opHours}h Op Period`}</Text>
                        <Badge size="xs" color="gray" variant="filled">Closed</Badge>
                      </Group>
                      <Group gap="xs">
                        <Badge size="xs" variant="outline" color="gray" leftSection={<IconClock size={10} />}>
                          {formatOpPeriod(act)}
                        </Badge>
                      </Group>
                    </div>
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      rightSection={<IconArrowRight size={14} />}
                      onClick={() => navigate(`/battlerhythm/activation/${act.id}`)}
                    >
                      View
                    </Button>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Collapse>
        </>
      )}

      {/* ── Create Activation Modal ────────────────────────────── */}
      <Modal
        opened={createOpened}
        onClose={closeCreate}
        title="Create Activation"
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Organization Name"
            placeholder="e.g. County EOC"
            value={newOrg}
            onChange={e => setNewOrg(e.currentTarget.value)}
          />
          <Select
            label="Operational Period"
            data={[
              { value: '12', label: '12-Hour Period' },
              { value: '24', label: '24-Hour Period' },
            ]}
            value={opPeriod}
            onChange={v => setOpPeriod(v ?? '12')}
          />
          <DateTimePicker
            label="Start Time"
            placeholder="Pick a start time"
            value={startTime}
            onChange={(v) => setStartTime(v ? new Date(v) : null)}
            clearable
          />
          <Select
            label="Apply Template (optional)"
            placeholder="Select a template"
            data={templateOptions}
            value={selectedTemplate}
            onChange={setSelectedTemplate}
            clearable
            searchable
          />
          <Text size="xs" c="dimmed">
            Selecting a template will automatically create scheduled events based on the template's configuration.
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={closeCreate}>Cancel</Button>
            <Button
              color="violet"
              onClick={handleCreate}
              loading={creating}
            >
              Create Activation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
